import type { AtAGlanceAcceptedResult } from './atAGlanceResultCache.ts';

export type AtAGlanceInputMatch = 'identical' | 'changed' | 'unknown';

export type AtAGlanceReuseFields = {
	resultAgeMs?: number;
	resultAgeCoverage?: 'known' | 'unknown';
	resultInputMatch?: AtAGlanceInputMatch;
	unavailableResultIndex?: number;
	unavailableInputMatch?: AtAGlanceInputMatch;
	unavailableOverviewCommit?: 'notObserved' | 'identicalInput' | 'changedInput' | 'unknownInput';
};

export type AtAGlanceCapturedOverviewCommit = {
	acceptedResult?: AtAGlanceAcceptedResult;
	qualify: () => void;
};

type UnavailableEpisode = {
	acceptedResult: AtAGlanceAcceptedResult;
	index: number;
	overviewCommit: NonNullable<AtAGlanceReuseFields['unavailableOverviewCommit']>;
};

export type AtAGlanceReuseObservation = {
	observeResult: (acceptedResult: AtAGlanceAcceptedResult | undefined) => void;
	captureOverviewCommit: (
		acceptedResult: AtAGlanceAcceptedResult | undefined,
	) => AtAGlanceCapturedOverviewCommit;
	fields: (
		currentBody: string | null,
		now: () => number,
		operationBody?: string,
	) => AtAGlanceReuseFields;
};

const inputMatch = (
	acceptedBody: string | undefined,
	currentBody: string | null,
): AtAGlanceInputMatch => {
	if (acceptedBody === undefined || currentBody === null) {
		return 'unknown';
	}
	if (acceptedBody === currentBody) {
		return 'identical';
	}
	return 'changed';
};

// CCINT-9574: accepted output can remain visible through changed input and failed work. Its age and
// post-unavailable display must follow the exact local record, not the latest request status.
export const createAtAGlanceReuseObservation = (): AtAGlanceReuseObservation => {
	let selectedResult: AtAGlanceAcceptedResult | undefined;
	let episode: UnavailableEpisode | undefined;
	return {
		observeResult: (acceptedResult) => {
			selectedResult = acceptedResult;
			if (
				acceptedResult?.output.status === 'UNAVAILABLE' &&
				episode?.acceptedResult !== acceptedResult
			) {
				episode = {
					acceptedResult,
					index: (episode?.index ?? 0) + 1,
					overviewCommit: 'notObserved',
				};
			}
		},
		captureOverviewCommit: (acceptedResult) => {
			const capturedEpisode = episode;
			return {
				acceptedResult,
				qualify: () => {
					if (
						!capturedEpisode ||
						capturedEpisode !== episode ||
						capturedEpisode.overviewCommit !== 'notObserved' ||
						!acceptedResult ||
						acceptedResult !== selectedResult ||
						acceptedResult === capturedEpisode.acceptedResult ||
						!acceptedResult.output.overview.length
					) {
						return;
					}
					const match = inputMatch(
						acceptedResult.requestBody,
						capturedEpisode.acceptedResult.requestBody,
					);
					if (match === 'identical') {
						capturedEpisode.overviewCommit = 'identicalInput';
					} else if (match === 'changed') {
						capturedEpisode.overviewCommit = 'changedInput';
					} else {
						capturedEpisode.overviewCommit = 'unknownInput';
					}
				},
			};
		},
		fields: (currentBody, now, operationBody) => {
			let resultAgeMs: number | undefined;
			const acceptedAt = selectedResult?.acceptedAtMs;
			if (acceptedAt !== undefined && Number.isFinite(acceptedAt) && acceptedAt >= 0) {
				try {
					const current = now();
					if (Number.isFinite(current) && current >= acceptedAt) {
						resultAgeMs = Math.round(current - acceptedAt);
					}
				} catch {
					// Invalid observation time leaves age unknown without changing result ownership.
				}
			}
			return {
				...(resultAgeMs === undefined ? {} : { resultAgeMs }),
				resultAgeCoverage: resultAgeMs === undefined ? 'unknown' : 'known',
				resultInputMatch: inputMatch(selectedResult?.requestBody, currentBody),
				...(episode
					? {
							unavailableResultIndex: episode.index,
							unavailableInputMatch: inputMatch(
								episode.acceptedResult.requestBody,
								operationBody ?? currentBody,
							),
							unavailableOverviewCommit: episode.overviewCommit,
						}
					: {}),
			};
		},
	};
};
